import { Archive, Ban, Boxes, CircleCheck, MapPinPlus, Package, PackageCheck, Plus, Truck } from 'lucide-react';

import type { DataViewActionSection } from 'components/DataView';
import { DeliveryState, TotadminPermission } from 'data/__generated__';

import { AbortDeliveriesPopup } from '../Popups/AbortDeliveriesPopup';
import { ArchiveDeliveriesPopup } from '../Popups/ArchiveDeliveriesPopup';
import { CreateBundlesPopup } from '../Popups/CreateBundlesPopup';
import { CreateDeliveryPopup } from '../Popups/CreateDeliveryPopup';
import { CreateSiteDeliveryPopup } from '../Popups/CreateSiteDeliveryPopup';
import { MarkAsDeliveredPopup } from '../Popups/MarkAsDeliveredPopup';
import { MarkAsInProgressPopup } from '../Popups/MarkAsInProgressPopup';
import { MarkAsPackagingPopup } from '../Popups/MarkAsPackagingPopup';
import { MarkAsReadyPopup } from '../Popups/MarkAsReadyPopup';

import type { DeliveryRow } from './deliveryViews';

/** Une transition ne s'offre que si TOUTES les livraisons sélectionnées peuvent la prendre. */
const whenAllAre =
    (...states: DeliveryState[]) =>
    (selected: DeliveryRow[]) =>
        selected.length > 0 && selected.every(({ state }) => states.includes(state));

const whenNoneIs =
    (...states: DeliveryState[]) =>
    (selected: DeliveryRow[]) =>
        selected.length > 0 && selected.every(({ state }) => !states.includes(state));

/**
 * La barre d'actions des livraisons, en config. Elle remplace le composant `DeliveryActionBar` et ses
 * huit booléens d'ouverture : la `DataView` porte l'état d'ouverture et monte les popups.
 */
export const DELIVERY_ACTIONS: DataViewActionSection<DeliveryRow>[] = [
    {
        title: 'Marquer comme',
        actions: [
            {
                key: 'packaging',
                requiredPermissions: [TotadminPermission.DeliveriesManage],
                icon: <Package />,
                label: 'en préparation',
                visibleWhen: whenAllAre(DeliveryState.Created),
                renderPopup: ({ isOpen, setIsOpen, rows, selectedKeys }) => (
                    <MarkAsPackagingPopup
                        deliveries={rows}
                        selectedDeliveryIds={selectedKeys}
                        isOpen={isOpen}
                        setIsOpen={setIsOpen}
                    />
                ),
            },
            {
                key: 'ready',
                requiredPermissions: [TotadminPermission.DeliveriesManage],
                icon: <PackageCheck />,
                label: 'prête(s)',
                visibleWhen: whenAllAre(DeliveryState.Packaging),
                renderPopup: ({ isOpen, setIsOpen, rows, selectedKeys }) => (
                    <MarkAsReadyPopup
                        deliveries={rows}
                        selectedDeliveryIds={selectedKeys}
                        isOpen={isOpen}
                        setIsOpen={setIsOpen}
                    />
                ),
            },
            {
                key: 'in-progress',
                requiredPermissions: [TotadminPermission.DeliveriesManage],
                icon: <Truck />,
                label: 'en livraison',
                visibleWhen: whenAllAre(DeliveryState.Ready),
                renderPopup: ({ isOpen, setIsOpen, rows, selectedKeys }) => (
                    <MarkAsInProgressPopup
                        deliveries={rows}
                        selectedDeliveryIds={selectedKeys}
                        isOpen={isOpen}
                        setIsOpen={setIsOpen}
                    />
                ),
            },
            {
                key: 'delivered',
                requiredPermissions: [TotadminPermission.DeliveriesManage],
                icon: <CircleCheck />,
                label: 'livrée(s)',
                visibleWhen: whenNoneIs(
                    DeliveryState.Aborted,
                    DeliveryState.Archived,
                    DeliveryState.Created,
                    DeliveryState.Delivered,
                    DeliveryState.Packaging,
                ),
                renderPopup: ({ isOpen, setIsOpen, rows, selectedKeys }) => (
                    <MarkAsDeliveredPopup
                        deliveries={rows}
                        selectedDeliveryIds={selectedKeys}
                        isOpen={isOpen}
                        setIsOpen={setIsOpen}
                    />
                ),
            },
            {
                key: 'aborted',
                requiredPermissions: [TotadminPermission.DeliveriesManage],
                icon: <Ban />,
                label: 'annulée(s)',
                visibleWhen: whenNoneIs(
                    DeliveryState.Aborted,
                    DeliveryState.Archived,
                    DeliveryState.Delivered,
                    DeliveryState.InProgress,
                ),
                renderPopup: ({ isOpen, setIsOpen, rows, selectedKeys }) => (
                    <AbortDeliveriesPopup
                        deliveries={rows}
                        selectedDeliveryIds={selectedKeys}
                        isOpen={isOpen}
                        setIsOpen={setIsOpen}
                    />
                ),
            },
            {
                key: 'archived',
                requiredPermissions: [TotadminPermission.DeliveriesManage],
                icon: <Archive />,
                label: 'archivée(s)',
                visibleWhen: whenNoneIs(
                    DeliveryState.Archived,
                    DeliveryState.Created,
                    DeliveryState.InProgress,
                    DeliveryState.NotDelivered,
                    DeliveryState.Packaging,
                    DeliveryState.Ready,
                ),
                renderPopup: ({ isOpen, setIsOpen, rows, selectedKeys }) => (
                    <ArchiveDeliveriesPopup
                        deliveries={rows}
                        selectedDeliveryIds={selectedKeys}
                        isOpen={isOpen}
                        setIsOpen={setIsOpen}
                    />
                ),
            },
        ],
    },
    {
        title: 'Autres actions',
        actions: [
            {
                key: 'create-delivery',
                requiredPermissions: [TotadminPermission.DeliveriesManage],
                icon: <Plus />,
                label: 'Créer une livraison freefood',
                renderPopup: ({ isOpen, setIsOpen }) => <CreateDeliveryPopup isOpen={isOpen} setIsOpen={setIsOpen} />,
            },
            {
                key: 'create-site-delivery',
                requiredPermissions: [TotadminPermission.DeliveriesManage],
                icon: <MapPinPlus />,
                label: 'Créer une livraison store',
                renderPopup: ({ isOpen, setIsOpen }) => (
                    <CreateSiteDeliveryPopup isOpen={isOpen} setIsOpen={setIsOpen} />
                ),
            },
            {
                key: 'create-bundles',
                requiredPermissions: [TotadminPermission.BundlesManage],
                icon: <Boxes />,
                label: 'Créer des bundles',
                renderPopup: ({ isOpen, setIsOpen }) => <CreateBundlesPopup isOpen={isOpen} setIsOpen={setIsOpen} />,
            },
        ],
    },
];
